import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../../contexts/UserContext';
import { isHostRole } from '../../../utils/roles';
import { getUpcomingMeetings } from '../../../services/meetingService';
import ScheduleMeetingModal from './ScheduleMeetingModal';

export default function UpcomingClasses() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const userId = currentUser?.id || currentUser?._id;
  const isHost = isHostRole(currentUser?.role);
  const canSchedule =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'sales';

  const isMeetingHost = (meeting) => {
    if (isHost) return true;
    if (currentUser?.role === 'sales') {
      return (meeting.participants || []).some(
        (p) => p.role === 'sales' && String(p.userId) === String(userId)
      );
    }
    return false;
  };

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [copiedToken, setCopiedToken] = useState(null);

  const copyJoinLink = useCallback((joinToken) => {
    const url = `${window.location.origin}/join/${joinToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(joinToken);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return undefined;
    setLoading(true);
    setError('');
    getUpcomingMeetings(userId)
      .then(({ meetings: fetched }) => {
        if (cancelled) return;
        setMeetings(fetched || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Unable to load schedule.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, refreshKey]);

  const handleEnterClassroom = (meeting) => {
    navigate('/classroom', { state: { classInfo: { id: meeting.id, title: meeting.title, detail: meeting.detail, participants: meeting.participants || [] } } });
  };

  return (
    <>
      <article className="info-card">
        <div className="info-card-header">
          <p className="eyebrow">Upcoming classes</p>
          <div className="info-card-header-actions">
            {canSchedule && (
              <button
                type="button"
                className="pill-btn subtle compact"
                onClick={() => setShowModal(true)}
              >
                + Schedule
              </button>
            )}
          </div>
        </div>

        {loading && <p className="muted-text">Loading schedule…</p>}
        {!loading && error && <p className="error-text">{error}</p>}
        {!loading && !error && meetings.length === 0 && (
          <p className="muted-text">No upcoming sessions scheduled.</p>
        )}

        {!loading && !error && meetings.length > 0 && (
          <ul>
            {meetings.map((item) => (
              <li key={item.id} className="class-row">
                <div>
                  <p className="info-title">
                    {item.title}
                    {item.isLive && <span className="live-badge">LIVE</span>}
                  </p>
                  <p className="muted-text">{item.detail}</p>
                </div>
                <div className="class-actions">
                  {/* LiveKit — primary path via join link */}
                  {item.joinToken && (
                    <button
                      type="button"
                      className="join-btn host-btn"
                      onClick={() => navigate(`/join/${item.joinToken}`)}
                      title="Enter session via LiveKit"
                    >
                      {isMeetingHost(item)
                        ? (item.isLive ? 'Rejoin' : 'Start')
                        : (item.isLive ? 'Join now' : 'Join')}
                    </button>
                  )}
                  {/* Copy join link */}
                  {item.joinToken && (
                    <button
                      type="button"
                      className="join-btn"
                      onClick={() => copyJoinLink(item.joinToken)}
                      title="Copy join link"
                    >
                      {copiedToken === item.joinToken ? 'Copied!' : 'Copy link'}
                    </button>
                  )}
                  {/* Zoom — kept during transition period */}
                  <button
                    type="button"
                    className="join-btn"
                    onClick={() => handleEnterClassroom(item)}
                    disabled={!isMeetingHost(item) && !item.isLive}
                    title="Enter via Zoom"
                  >
                    {isMeetingHost(item) ? (item.isLive ? 'Rejoin Zoom' : 'Start Zoom') : (item.isLive ? 'Join Zoom' : 'Not started')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </article>

      {showModal && (
        <ScheduleMeetingModal
          onClose={() => setShowModal(false)}
          onCreated={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </>
  );
}
